import { useState, useEffect, createContext, useContext, useRef, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster, toast } from "sonner";
import { Helmet } from "react-helmet-async";
import { 
  ShoppingCart, 
  X, 
  Plus, 
  Minus, 
  Trash2, 
  Search, 
  Menu, 
  Phone, 
  MapPin, 
  Mail,
  Leaf,
  Droplets,
  Sparkles,
  Package,
  ChevronRight,
  ChevronLeft,
  Star,
  Check,
  ArrowRight,
  Pencil,
  Upload,
  Image,
  Save,
  Settings
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import AdminDashboard from "./AdminDashboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// UPI Payment Details
const PHONEPE_QR_IMAGE = "/api/storage/uploads/ef565b70-2025-4de1-a72e-e90576c0cf33.jpeg";
const UPI_ID = "6398806603@ibl";
const PAYEE_NAME = "Duwa Enterprises";

// Generate UPI Payment URL with amount
const generateUPIUrl = (upiId, payeeName, amount) => {
  const params = new URLSearchParams({
    pa: upiId,
    pn: payeeName,
    am: amount.toFixed(2),
    cu: "INR"
  });
  return `upi://pay?${params.toString()}`;
};

// Cart Context
const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within CartProvider");
  }
  return context;
};

const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);

  const addToCart = (product) => {
    setCartItems(prev => {
      const existing = prev.find(item => item.product_id === product.id);
      if (existing) {
        return prev.map(item =>
          item.product_id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, {
        product_id: product.id,
        name: product.name,
        price: product.price,
        quantity: 1,
        image_url: product.image_url
      }];
    });
    toast.success("Added to cart!");
  };

  const removeFromCart = (productId) => {
    setCartItems(prev => prev.filter(item => item.product_id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCartItems(prev =>
      prev.map(item =>
        item.product_id === productId ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => setCartItems([]);

  const cartTotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{
      cartItems, addToCart, removeFromCart, updateQuantity, clearCart,
      cartTotal, cartCount, isCartOpen, setIsCartOpen
    }}>
      {children}
    </CartContext.Provider>
  );
};

// Header Component
const Header = () => {
  const { cartCount, setIsCartOpen } = useCart();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-white/90 backdrop-blur-xl shadow-sm" : "bg-transparent"
      }`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_design-hosting-flow/artifacts/bui4e431_ChatGPT%20Image%20Feb%2025%2C%202026%2C%2007_13_10%20AM.png" 
              alt="Duwa Enterprises" 
              className="h-12 md:h-14 w-auto"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-[#1A1A1A] hover:text-[#8FA893] transition-colors font-medium" data-testid="nav-home">Home</Link>
            <Link to="/products" className="text-[#1A1A1A] hover:text-[#8FA893] transition-colors font-medium" data-testid="nav-products">Products</Link>
            <Link to="/about" className="text-[#1A1A1A] hover:text-[#8FA893] transition-colors font-medium" data-testid="nav-about">About</Link>
            <Link to="/contact" className="text-[#1A1A1A] hover:text-[#8FA893] transition-colors font-medium" data-testid="nav-contact">Contact</Link>
          </nav>

          <div className="flex items-center gap-4">
            <button 
              onClick={() => setIsCartOpen(true)}
              className="relative p-2 hover:bg-[#8FA893]/10 rounded-full transition-colors"
              data-testid="cart-button"
            >
              <ShoppingCart className="w-6 h-6 text-[#1A2F23]" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#1A2F23] text-white text-xs rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>
            <button 
              className="md:hidden p-2 hover:bg-[#8FA893]/10 rounded-full"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              <Menu className="w-6 h-6 text-[#1A2F23]" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t"
          >
            <nav className="flex flex-col p-4 gap-4">
              <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="text-[#1A1A1A] py-2">Home</Link>
              <Link to="/products" onClick={() => setIsMobileMenuOpen(false)} className="text-[#1A1A1A] py-2">Products</Link>
              <Link to="/about" onClick={() => setIsMobileMenuOpen(false)} className="text-[#1A1A1A] py-2">About</Link>
              <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)} className="text-[#1A1A1A] py-2">Contact</Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

// Cart Drawer Component
const CartDrawer = () => {
  const { cartItems, cartTotal, isCartOpen, setIsCartOpen, updateQuantity, removeFromCart } = useCart();
  const navigate = useNavigate();

  return (
    <AnimatePresence>
      {isCartOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50"
            onClick={() => setIsCartOpen(false)}
          />
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-50 shadow-2xl flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-heading font-semibold text-[#1A2F23]">Your Cart</h2>
              <button 
                onClick={() => setIsCartOpen(false)}
                className="p-2 hover:bg-[#F5F5F4] rounded-full"
                data-testid="close-cart-button"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {cartItems.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
                <ShoppingCart className="w-16 h-16 text-[#9CA3AF] mb-4" />
                <p className="text-[#525252] mb-4">Your cart is empty</p>
                <button 
                  onClick={() => {
                    setIsCartOpen(false);
                    navigate("/products");
                  }}
                  className="bg-[#1A2F23] text-white px-6 py-3 rounded-full hover:bg-[#1A2F23]/90 transition-colors"
                >
                  Shop Now
                </button>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                  {cartItems.map(item => (
                    <div key={item.product_id} className="flex gap-4 bg-[#F9F9F7] p-4 rounded-xl" data-testid={`cart-item-${item.product_id}`}>
                      <img 
                        src={item.image_url} 
                        alt={item.name}
                        className="w-20 h-20 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <h3 className="font-medium text-[#1A1A1A] text-sm mb-1">{item.name}</h3>
                        <p className="text-[#1A2F23] font-semibold">₹{item.price}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <button 
                            onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                            className="w-7 h-7 flex items-center justify-center bg-white rounded-full border hover:bg-[#F5F5F4]"
                            data-testid={`decrease-qty-${item.product_id}`}
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="w-8 text-center text-sm">{item.quantity}</span>
                          <button 
                            onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                            className="w-7 h-7 flex items-center justify-center bg-white rounded-full border hover:bg-[#F5F5F4]"
                            data-testid={`increase-qty-${item.product_id}`}
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      <button 
                        onClick={() => removeFromCart(item.product_id)}
                        className="p-2 hover:bg-red-50 rounded-full self-start"
                        data-testid={`remove-item-${item.product_id}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="p-6 border-t bg-white">
                  <div className="flex justify-between mb-4">
                    <span className="text-[#525252]">Subtotal</span>
                    <span className="font-semibold text-[#1A2F23]">₹{cartTotal.toFixed(2)}</span>
                  </div>
                  <button 
                    onClick={() => {
                      setIsCartOpen(false);
                      navigate("/checkout");
                    }}
                    className="w-full bg-[#1A2F23] text-white py-4 rounded-full hover:bg-[#1A2F23]/90 transition-all font-medium"
                    data-testid="checkout-button"
                  >
                    Proceed to Checkout
                  </button>
                </div>
              </>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// Product Card Component
const ProductCard = ({ product }) => {
  const { addToCart } = useCart();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="group relative bg-white rounded-2xl overflow-hidden border border-[#E5E5E5] transition-all hover:shadow-xl hover:border-[#8FA893]/50"
      data-testid={`product-card-${product.id}`}
    >
      <Link to={`/product/${product.slug}`}>
        <div className="relative aspect-square overflow-hidden bg-[#F9F9F7]">
          <img 
            src={product.image_url} 
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          {product.discount_text && (
            <span className="absolute top-4 left-4 bg-[#8FA893] text-white text-xs font-medium px-3 py-1 rounded-full">
              {product.discount_text}
            </span>
          )}
        </div>
      </Link>
      <div className="p-5">
        <p className="text-xs text-[#8FA893] font-medium uppercase tracking-wide mb-2">{product.category}</p>
        <Link to={`/product/${product.slug}`}>
          <h3 className="font-heading font-medium text-[#1A1A1A] mb-2 line-clamp-2 hover:text-[#8FA893] transition-colors">
            {product.name}
          </h3>
        </Link>
        <p className="text-sm text-[#525252] mb-3 line-clamp-2">{product.short_description}</p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-lg font-semibold text-[#1A2F23]">₹{product.price}</span>
            {product.original_price > product.price && (
              <span className="text-sm text-[#9CA3AF] line-through">₹{product.original_price}</span>
            )}
          </div>
          <button 
            onClick={(e) => {
              e.preventDefault();
              addToCart(product);
            }}
            className="bg-[#1A2F23] text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-[#1A2F23]/90 transition-all hover:-translate-y-0.5"
            data-testid={`add-to-cart-${product.id}`}
          >
            Add
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// Product Catalogue Banner with auto-scroll carousel
const ProductCatalogueBanner = ({ products, loading }) => {
  const { addToCart } = useCart();
  const scrollRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const cardWidth = 340;
  const gap = 24;

  const totalProducts = products.length;

  const scrollToIndex = useCallback((index) => {
    if (!scrollRef.current || totalProducts === 0) return;
    const clampedIndex = ((index % totalProducts) + totalProducts) % totalProducts;
    setActiveIndex(clampedIndex);
    const target = clampedIndex * (cardWidth + gap);
    const el = scrollRef.current;
    // Cross-browser smooth scroll (Safari compatible)
    const start = el.scrollLeft;
    const distance = target - start;
    const duration = 400;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const ease = progress < 0.5 ? 2 * progress * progress : -1 + (4 - 2 * progress) * progress;
      el.scrollLeft = start + distance * ease;
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [totalProducts, cardWidth, gap]);

  // Auto-scroll every 5 seconds (Safari-safe with ref to avoid stale closure)
  const activeRef = useRef(activeIndex);
  activeRef.current = activeIndex;
  
  useEffect(() => {
    if (isPaused || totalProducts === 0) return;
    const interval = setInterval(() => {
      scrollToIndex(activeRef.current + 1);
    }, 5000);
    return () => clearInterval(interval);
  }, [isPaused, totalProducts, scrollToIndex]);

  const handleBannerPrev = () => scrollToIndex(activeIndex - 1);
  const handleBannerNext = () => scrollToIndex(activeIndex + 1);

  // Track which image is shown per product card
  const [cardImageIndexes, setCardImageIndexes] = useState({});

  const getCardImageIndex = (productId) => cardImageIndexes[productId] || 0;

  const handleCardPrev = (product) => {
    const images = product.images || [product.image_url];
    if (images.length <= 1) return;
    setCardImageIndexes(prev => {
      const current = prev[product.id] || 0;
      return { ...prev, [product.id]: current === 0 ? images.length - 1 : current - 1 };
    });
  };

  const handleCardNext = (product) => {
    const images = product.images || [product.image_url];
    if (images.length <= 1) return;
    setCardImageIndexes(prev => {
      const current = prev[product.id] || 0;
      return { ...prev, [product.id]: current === images.length - 1 ? 0 : current + 1 };
    });
  };

  if (loading) {
    return (
      <section className="py-16 md:py-24 bg-white" data-testid="product-catalogue-loading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-6 overflow-hidden">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="min-w-[320px] bg-[#F5F5F4] rounded-2xl h-[440px] animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className="py-16 md:py-24 bg-white relative"
      data-testid="product-catalogue-banner"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-col md:flex-row md:items-end md:justify-between mb-10"
        >
          <div>
            <h2 className="text-4xl md:text-5xl font-heading font-medium text-[#1A2F23] mb-3">Product Catalog</h2>
            <p className="text-[#525252] text-lg">Browse our complete range of herbal hair care solutions</p>
          </div>
          <Link
            to="/products"
            className="mt-4 md:mt-0 text-[#1A2F23] font-medium hover:text-[#8FA893] transition-colors inline-flex items-center gap-2"
            data-testid="view-all-products"
          >
            View All <ChevronRight className="w-5 h-5" />
          </Link>
        </motion.div>

        {/* Carousel wrapper */}
        <div className="relative group/banner">
          {/* Left banner arrow */}
          <button
            onClick={handleBannerPrev}
            className="absolute -left-2 md:-left-5 top-1/2 -translate-y-1/2 z-20 w-11 h-11 bg-white border border-[#E5E5E5] rounded-full flex items-center justify-center shadow-lg hover:bg-[#1A2F23] hover:text-white hover:border-[#1A2F23] transition-all opacity-0 group-hover/banner:opacity-100"
            data-testid="catalogue-prev-btn"
            aria-label="Previous products"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          {/* Right banner arrow */}
          <button
            onClick={handleBannerNext}
            className="absolute -right-2 md:-right-5 top-1/2 -translate-y-1/2 z-20 w-11 h-11 bg-white border border-[#E5E5E5] rounded-full flex items-center justify-center shadow-lg hover:bg-[#1A2F23] hover:text-white hover:border-[#1A2F23] transition-all opacity-0 group-hover/banner:opacity-100"
            data-testid="catalogue-next-btn"
            aria-label="Next products"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {/* Scrollable track */}
          <div
            ref={scrollRef}
            className="flex gap-6 overflow-x-auto scroll-smooth pb-4 catalogue-scrollbar"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {products.map((product, idx) => {
              const images = product.images && product.images.length > 0 ? product.images : [product.image_url];
              const currentImgIdx = getCardImageIndex(product.id);
              const hasMultipleImages = images.length > 1;
              return (
              <div
                key={product.id}
                className="min-w-[300px] sm:min-w-[320px] md:min-w-[340px] max-w-[340px] flex-shrink-0 group/card relative"
                data-testid={`catalogue-card-${product.id}`}
              >
                <div className="bg-white rounded-2xl overflow-hidden border border-[#E5E5E5] transition-all hover:shadow-xl hover:border-[#8FA893]/50 h-full flex flex-col">
                  {/* Product image */}
                  <Link to={`/product/${product.slug}`}>
                    <div className="relative aspect-square overflow-hidden bg-[#F9F9F7]">
                      <img
                        src={images[currentImgIdx]}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-500"
                      />
                      {product.discount_text && (
                        <span className="absolute top-4 left-4 bg-[#8FA893] text-white text-xs font-medium px-3 py-1 rounded-full">
                          {product.discount_text}
                        </span>
                      )}

                      {/* Per-card left arrow - cycles through this product's images */}
                      {hasMultipleImages && (
                      <button
                        onClick={(e) => { e.preventDefault(); handleCardPrev(product); }}
                        className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white/90 backdrop-blur rounded-full flex items-center justify-center shadow-md hover:bg-[#1A2F23] hover:text-white transition-all opacity-0 group-hover/card:opacity-100"
                        data-testid={`card-prev-${product.id}`}
                        aria-label="Previous image"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      )}

                      {/* Per-card right arrow - cycles through this product's images */}
                      {hasMultipleImages && (
                      <button
                        onClick={(e) => { e.preventDefault(); handleCardNext(product); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white/90 backdrop-blur rounded-full flex items-center justify-center shadow-md hover:bg-[#1A2F23] hover:text-white transition-all opacity-0 group-hover/card:opacity-100"
                        data-testid={`card-next-${product.id}`}
                        aria-label="Next image"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                      )}

                      {/* Image dots indicator */}
                      {hasMultipleImages && (
                        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                          {images.map((_, i) => (
                            <span key={i} className={`w-1.5 h-1.5 rounded-full transition-all ${i === currentImgIdx ? "bg-white w-4" : "bg-white/50"}`} />
                          ))}
                        </div>
                      )}
                    </div>
                  </Link>

                  {/* Product info */}
                  <div className="p-5 flex flex-col flex-1">
                    <p className="text-xs text-[#8FA893] font-medium uppercase tracking-wide mb-2">{product.category}</p>
                    <Link to={`/product/${product.slug}`}>
                      <h3 className="font-heading font-medium text-[#1A1A1A] mb-2 line-clamp-2 hover:text-[#8FA893] transition-colors">
                        {product.name}
                      </h3>
                    </Link>
                    <p className="text-sm text-[#525252] mb-3 line-clamp-2">{product.short_description}</p>
                    <div className="mt-auto flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-semibold text-[#1A2F23]">₹{product.price}</span>
                        {product.original_price > product.price && (
                          <span className="text-sm text-[#9CA3AF] line-through">₹{product.original_price}</span>
                        )}
                      </div>
                      <button
                        onClick={() => addToCart(product)}
                        className="bg-[#1A2F23] text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-[#1A2F23]/90 transition-all hover:-translate-y-0.5"
                        data-testid={`catalogue-add-to-cart-${product.id}`}
                      >
                        Add
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
            })}
          </div>

          {/* Dot indicators */}
          <div className="flex justify-center gap-2 mt-6" data-testid="catalogue-dots">
            {products.map((_, idx) => (
              <button
                key={idx}
                onClick={() => scrollToIndex(idx)}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  idx === activeIndex
                    ? "bg-[#1A2F23] w-7"
                    : "bg-[#D4D4D4] hover:bg-[#8FA893]"
                }`}
                data-testid={`catalogue-dot-${idx}`}
                aria-label={`Go to product ${idx + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Home Page
const HomePage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
      } catch (error) {
        console.error("Failed to fetch products:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="home-page">
      <Helmet>
        <title>Duwa Enterprises - Herbal Hair Care & Beauty Products | Shop Online</title>
        <meta name="description" content="Shop authentic herbal hair care & beauty products at Duwa Enterprises. 100% natural ingredients, visible results. Hair growth serums, herbal oils & more. Free shipping across India." />
        <link rel="canonical" href={`${window.location.origin}/`} />
        <meta property="og:title" content="Duwa Enterprises - Herbal Hair Care & Beauty Products" />
        <meta property="og:description" content="Shop authentic herbal hair care & beauty products. Pure herbs, real care, visible results." />
        <meta property="og:url" content={`${window.location.origin}/`} />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": "Duwa Enterprises",
        "description": "Authentic herbal hair care and beauty products. Pure herbs, real care, visible results.",
        "url": window.location.origin,
        "telephone": "+916398806603",
        "email": "asrafsalmani98@gmail.com",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Big Boss 2 Hair Salon, Arvind Nagar Karwar Road, Near ESI Hospital",
          "addressLocality": "Hubli",
          "addressRegion": "Karnataka",
          "postalCode": "580024",
          "addressCountry": "IN"
        },
        "priceRange": "$$",
        "openingHours": ["Mo-Sa 10:00-20:00", "Su 11:00-18:00"]
      }) }} />
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="inline-block bg-[#8FA893]/20 text-[#1A2F23] px-4 py-2 rounded-full text-sm font-medium mb-6">
                100% Natural Herbal Products
              </span>
              <h1 className="text-5xl md:text-7xl font-heading font-medium text-[#1A2F23] leading-tight mb-6">
                Pure Herbs.<br />
                <span className="text-[#8FA893]">Real Care.</span><br />
                Visible Results.
              </h1>
              <p className="text-lg text-[#525252] mb-8 max-w-lg">
                Discover our range of authentic herbal hair care products. Crafted with traditional wisdom and modern science for stronger, healthier hair.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link 
                  to="/products"
                  className="bg-[#1A2F23] text-white px-8 py-4 rounded-full font-medium hover:bg-[#1A2F23]/90 transition-all hover:-translate-y-0.5 shadow-lg hover:shadow-xl inline-flex items-center gap-2"
                  data-testid="hero-shop-now"
                >
                  Shop Now <ArrowRight className="w-5 h-5" />
                </Link>
                <Link 
                  to="/about"
                  className="border-2 border-[#1A2F23] text-[#1A2F23] px-8 py-4 rounded-full font-medium hover:bg-[#1A2F23] hover:text-white transition-all"
                  data-testid="hero-learn-more"
                >
                  Learn More
                </Link>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-[#8FA893]/20 rounded-[60px] transform rotate-6"></div>
                <img 
                  src="https://customer-assets.emergentagent.com/job_c681bc13-184d-4aa5-90d7-78fc316342aa/artifacts/60ilhkav_ChatGPT%20Image%20Feb%2018%2C%202026%2C%2006_19_16%20PM.png"
                  alt="Duwa Enterprises Herbal Products"
                  className="relative rounded-[60px] w-full object-cover shadow-2xl"
                  style={{ maxHeight: "600px" }}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Scrolling Ticker - FLASH SALE */}
      <div className="bg-gradient-to-r from-[#1A2F23] via-[#2D4A3E] to-[#1A2F23] py-4 overflow-hidden relative">
        <div className="absolute inset-0 opacity-30" style={{backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='20' height='20' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='2' cy='2' r='1' fill='rgba(255,255,255,0.3)'/%3E%3C/svg%3E\")"}}></div>
        <div className="animate-marquee-fast whitespace-nowrap flex items-center gap-8 relative">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex items-center gap-8">
              <span className="bg-yellow-400 text-black px-4 py-1 rounded-full font-bold text-sm animate-pulse">FLASH SALE</span>
              <span className="text-white font-bold text-lg">25% OFF</span>
              <span className="text-yellow-300 text-2xl">⚡</span>
              <span className="text-[#8FA893] font-semibold">100% Herbal Products</span>
              <span className="text-yellow-300 text-2xl">🌿</span>
              <span className="bg-gradient-to-r from-green-400 to-emerald-500 text-white px-4 py-1 rounded-full font-bold text-sm">LIMITED TIME</span>
              <span className="text-yellow-300 text-2xl">🎉</span>
            </div>
          ))}
        </div>
      </div>

      {/* Product Catalogue Banner */}
      <ProductCatalogueBanner products={products} loading={loading} />

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[#1A2F23]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-heading font-medium text-white mb-6">
              Ready for Healthier Hair?
            </h2>
            <p className="text-[#8FA893] text-lg mb-8 max-w-2xl mx-auto">
              Join thousands of satisfied customers who have transformed their hair with our herbal products.
            </p>
            <Link 
              to="/products"
              className="inline-flex items-center gap-2 bg-white text-[#1A2F23] px-8 py-4 rounded-full font-medium hover:bg-[#F9F9F7] transition-all hover:-translate-y-0.5 shadow-lg"
              data-testid="cta-shop-now"
            >
              Shop Now <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

// Products Page
const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
      } catch (error) {
        console.error("Failed to fetch products:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  return (
    <div className="min-h-screen bg-[#F9F9F7] pt-24" data-testid="products-page">
      <Helmet>
        <title>All Herbal Hair Care Products - Duwa Enterprises</title>
        <meta name="description" content="Browse our complete range of herbal hair care products. Hair growth serums, herbal oils, combo packs & more. 100% natural, no harmful chemicals. Shop now at Duwa Enterprises." />
        <link rel="canonical" href={`${window.location.origin}/products`} />
        <meta property="og:title" content="All Herbal Hair Care Products - Duwa Enterprises" />
        <meta property="og:description" content="Browse our complete range of herbal hair care products. 100% natural, no harmful chemicals." />
        <meta property="og:url" content={`${window.location.origin}/products`} />
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-heading font-medium text-[#1A2F23] mb-4">All Products</h1>
          <p className="text-[#525252] text-lg">Explore our complete range of herbal hair care products</p>
        </motion.div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl h-96 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Product Detail Page
const ProductDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // Check if admin mode is enabled (via URL param or localStorage)
    const urlParams = new URLSearchParams(window.location.search);
    const adminMode = urlParams.get('admin') === 'true' || localStorage.getItem('adminMode') === 'true';
    setIsAdmin(adminMode);
  }, []);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${slug}`);
      setProduct(response.data);
    } catch (error) {
      console.error("Failed to fetch product:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProduct();
  }, [slug]);

  useEffect(() => {
    if (product) {
      document.title = product.name + " - Buy Online | Duwa Enterprises";
      const setMeta = (attr, key, content) => {
        let el = document.querySelector(`meta[${attr}="${key}"]`);
        if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
        el.setAttribute("content", content);
      };
      setMeta("name", "description", "Buy " + product.name + " online at Duwa Enterprises. " + (product.short_description || "Premium herbal hair care product") + ". 100% natural ingredients.");
      setMeta("property", "og:title", product.name + " - Duwa Enterprises");
      setMeta("property", "og:description", product.short_description || product.description?.slice(0, 150) || "Premium herbal hair care product");
      setMeta("property", "og:url", window.location.origin + "/product/" + product.slug);
      if (product.image_url) setMeta("property", "og:image", product.image_url);
    }
  }, [product]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("adminToken");
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  };

  const handleDeleteProduct = async () => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    
    try {
      await axios.delete(`${API}/admin/products/${product.id}`, getAuthHeaders());
      toast.success("Product deleted successfully");
      navigate("/products");
    } catch (error) {
      console.error("Failed to delete product:", error);
      if (error.response?.status === 401) {
        toast.error("Please login as admin first");
        navigate("/admin");
      } else {
        toast.error("Failed to delete product");
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] pt-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#1A2F23]"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] pt-24 flex items-center justify-center">
        <p className="text-[#525252]">Product not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7] pt-24" data-testid="product-detail-page">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Product",
        "name": product.name,
        "description": (product.short_description || product.description || "").slice(0, 200),
        "image": product.image_url || "",
        "brand": { "@type": "Brand", "name": "Duwa Enterprises" },
        "offers": {
          "@type": "Offer",
          "price": product.price,
          "priceCurrency": "INR",
          "availability": "https://schema.org/InStock",
          "seller": { "@type": "Organization", "name": "Duwa Enterprises" }
        }
      }) }} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-2 gap-12">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <div className="sticky top-28">
              <div className="bg-white rounded-2xl overflow-hidden shadow-lg">
                <img 
                  src={selectedImage || product.image_url} 
                  alt={product.name}
                  className="w-full aspect-square object-cover"
                  data-testid="product-main-image"
                />
              </div>
              {product.images?.length > 1 && (
                <div className="flex gap-3 mt-4 overflow-x-auto pb-2">
                  {product.images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImage(img)}
                      className={`w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all ${
                        (selectedImage || product.image_url) === img
                          ? "border-[#1A2F23] shadow-md"
                          : "border-[#E5E5E5] hover:border-[#8FA893]"
                      }`}
                      data-testid={`product-thumb-${idx}`}
                    >
                      <img src={img} alt={`${product.name} view ${idx + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            {/* Admin Controls */}
            {isAdmin && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-yellow-800">Admin Mode</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowEditModal(true)}
                      className="flex items-center gap-1 px-3 py-1.5 bg-[#1A2F23] text-white rounded-lg text-sm hover:bg-[#1A2F23]/90"
                      data-testid="admin-edit-product"
                    >
                      <Pencil className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={handleDeleteProduct}
                      className="flex items-center gap-1 px-3 py-1.5 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600"
                      data-testid="admin-delete-product"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            )}

            {product.discount_text && (
              <span className="inline-block bg-[#8FA893] text-white text-sm font-medium px-4 py-1 rounded-full mb-4">
                {product.discount_text}
              </span>
            )}
            <h1 className="text-3xl md:text-4xl font-heading font-medium text-[#1A2F23] mb-4">
              {product.name}
            </h1>
            <div className="flex items-center gap-3 mb-6">
              <span className="text-3xl font-semibold text-[#1A2F23]">₹{product.price}</span>
              {product.original_price > product.price && (
                <span className="text-xl text-[#9CA3AF] line-through">₹{product.original_price}</span>
              )}
            </div>

            <p className="text-[#525252] leading-relaxed mb-8">
              {product.description}
            </p>

            {product.benefits?.length > 0 && (
              <div className="mb-8">
                <h3 className="font-heading font-medium text-[#1A2F23] mb-4">Key Benefits</h3>
                <ul className="space-y-2">
                  {product.benefits.map((benefit, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#8FA893] mt-0.5 flex-shrink-0" />
                      <span className="text-[#525252]">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {product.how_to_use && (
              <div className="mb-8 bg-[#F5F5F4] p-6 rounded-xl">
                <h3 className="font-heading font-medium text-[#1A2F23] mb-2">How to Use</h3>
                <p className="text-[#525252]">{product.how_to_use}</p>
              </div>
            )}

            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center border rounded-full">
                <button 
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="p-3 hover:bg-[#F5F5F4] rounded-l-full"
                  data-testid="decrease-quantity"
                >
                  <Minus className="w-5 h-5" />
                </button>
                <span className="w-12 text-center font-medium">{quantity}</span>
                <button 
                  onClick={() => setQuantity(quantity + 1)}
                  className="p-3 hover:bg-[#F5F5F4] rounded-r-full"
                  data-testid="increase-quantity"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="flex gap-3">
              <button 
                onClick={() => {
                  for (let i = 0; i < quantity; i++) {
                    addToCart(product);
                  }
                }}
                className="flex-1 bg-white border-2 border-[#1A2F23] text-[#1A2F23] py-4 rounded-full font-medium hover:bg-[#F5F5F4] transition-all"
                data-testid="add-to-cart-detail"
              >
                Add to Cart
              </button>
              <button 
                onClick={() => {
                  for (let i = 0; i < quantity; i++) {
                    addToCart(product);
                  }
                  navigate("/checkout");
                }}
                className="flex-1 bg-[#1A2F23] text-white py-4 rounded-full font-medium hover:bg-[#1A2F23]/90 transition-all hover:-translate-y-0.5 shadow-lg"
                data-testid="buy-now-button"
              >
                Buy Now - ₹{(product.price * quantity).toFixed(2)}
              </button>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Edit Product Modal */}
      <AnimatePresence>
        {showEditModal && product && (
          <ProductEditModal
            product={product}
            onClose={() => setShowEditModal(false)}
            onSave={() => {
              fetchProduct();
              setShowEditModal(false);
              toast.success("Product updated successfully");
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Product Edit Modal (for individual product page)
const ProductEditModal = ({ product, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: product?.name || "",
    description: product?.description || "",
    short_description: product?.short_description || "",
    price: product?.price || "",
    original_price: product?.original_price || "",
    discount_text: product?.discount_text || "",
    category: product?.category || "Hair Oil",
    image_url: product?.image_url || "",
    images: product?.images || [],
    benefits: product?.benefits || [""],
    how_to_use: product?.how_to_use || "",
    in_stock: product?.in_stock ?? true
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleBenefitChange = (index, value) => {
    const newBenefits = [...formData.benefits];
    newBenefits[index] = value;
    setFormData(prev => ({ ...prev, benefits: newBenefits }));
  };

  const addBenefit = () => {
    setFormData(prev => ({ ...prev, benefits: [...prev.benefits, ""] }));
  };

  const removeBenefit = (index) => {
    const newBenefits = formData.benefits.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, benefits: newBenefits }));
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem("adminToken");
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const uploadData = new FormData();
    uploadData.append("file", file);

    try {
      const authHeaders = getAuthHeaders();
      const response = await axios.post(`${API}/admin/upload`, uploadData, {
        headers: { 
          ...authHeaders.headers,
          "Content-Type": "multipart/form-data" 
        }
      });
      
      const imageUrl = `${BACKEND_URL}${response.data.url}`;
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, imageUrl],
        image_url: prev.image_url || imageUrl
      }));
      toast.success("Image uploaded successfully");
    } catch (error) {
      console.error("Failed to upload image:", error);
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const handleImageUrlAdd = () => {
    const url = prompt("Enter image URL:");
    if (url) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, url],
        image_url: prev.image_url || url
      }));
    }
  };

  const removeImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      images: newImages,
      image_url: newImages[0] || ""
    }));
  };

  const setMainImage = (imageUrl) => {
    setFormData(prev => ({ ...prev, image_url: imageUrl }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const payload = {
      ...formData,
      price: parseFloat(formData.price),
      original_price: parseFloat(formData.original_price) || parseFloat(formData.price),
      benefits: formData.benefits.filter(b => b.trim() !== "")
    };

    try {
      await axios.put(`${API}/admin/products/${product.id}`, payload, getAuthHeaders());
      onSave();
    } catch (error) {
      console.error("Failed to save product:", error);
      toast.error("Failed to save product");
    } finally {
      setSaving(false);
    }
  };

  const categories = ["Hair Oil", "Hair Serum", "Shampoo", "Herbal Powder", "Combo Pack", "Tools"];

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50"
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="fixed inset-4 md:inset-10 bg-white rounded-2xl z-50 overflow-hidden flex flex-col"
        data-testid="product-edit-modal"
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-[#1A2F23]">Edit Product</h2>
          <button onClick={onClose} className="p-2 hover:bg-[#F5F5F4] rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Basic Info */}
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-[#525252] mb-2">Product Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-[#E5E5E5] focus:outline-none focus:ring-2 focus:ring-[#1A2F23]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#525252] mb-2">Short Description *</label>
                <input
                  type="text"
                  name="short_description"
                  value={formData.short_description}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-[#E5E5E5] focus:outline-none focus:ring-2 focus:ring-[#1A2F23]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#525252] mb-2">Full Description *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg border border-[#E5E5E5] focus:outline-none focus:ring-2 focus:ring-[#1A2F23]"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#525252] mb-2">Price (₹) *</label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-[#E5E5E5] focus:outline-none focus:ring-2 focus:ring-[#1A2F23]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#525252] mb-2">Original Price (₹)</label>
                  <input
                    type="number"
                    name="original_price"
                    value={formData.original_price}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-[#E5E5E5] focus:outline-none focus:ring-2 focus:ring-[#1A2F23]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#525252] mb-2">Category *</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-[#E5E5E5] focus:outline-none focus:ring-2 focus:ring-[#1A2F23]"
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#525252] mb-2">Discount Text</label>
                  <input
                    type="text"
                    name="discount_text"
                    value={formData.discount_text}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-[#E5E5E5] focus:outline-none focus:ring-2 focus:ring-[#1A2F23]"
                    placeholder="25% OFF"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#525252] mb-2">How to Use</label>
                <textarea
                  name="how_to_use"
                  value={formData.how_to_use}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-4 py-3 rounded-lg border border-[#E5E5E5] focus:outline-none focus:ring-2 focus:ring-[#1A2F23]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#525252] mb-2">Key Benefits</label>
                <div className="space-y-2">
                  {formData.benefits.map((benefit, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={benefit}
                        onChange={(e) => handleBenefitChange(index, e.target.value)}
                        className="flex-1 px-4 py-2 rounded-lg border border-[#E5E5E5] focus:outline-none focus:ring-2 focus:ring-[#1A2F23]"
                        placeholder={`Benefit ${index + 1}`}
                      />
                      {formData.benefits.length > 1 && (
                        <button type="button" onClick={() => removeBenefit(index)} className="p-2 hover:bg-red-50 rounded-lg">
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={addBenefit} className="text-sm text-[#1A2F23] hover:underline flex items-center gap-1">
                    <Plus className="w-4 h-4" /> Add Benefit
                  </button>
                </div>
              </div>
            </div>

            {/* Right Column - Images */}
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-[#525252] mb-2">Product Images</label>
                <p className="text-sm text-[#9CA3AF] mb-4">Upload images or add URLs. Click to set main image.</p>
                
                <div className="flex gap-3 mb-4">
                  <label className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-[#E5E5E5] rounded-lg cursor-pointer hover:border-[#1A2F23] hover:bg-[#F9F9F7] transition-colors">
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploading} />
                    {uploading ? <span className="text-[#9CA3AF]">Uploading...</span> : <><Upload className="w-5 h-5 text-[#525252]" /><span className="text-[#525252]">Upload</span></>}
                  </label>
                  <button type="button" onClick={handleImageUrlAdd} className="flex items-center gap-2 px-4 py-3 border border-[#E5E5E5] rounded-lg hover:bg-[#F9F9F7]">
                    <Image className="w-5 h-5 text-[#525252]" /><span className="text-[#525252]">Add URL</span>
                  </button>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  {formData.images.map((img, index) => (
                    <div
                      key={index}
                      className={`relative group aspect-square rounded-lg overflow-hidden border-2 cursor-pointer transition-all ${formData.image_url === img ? "border-[#1A2F23] ring-2 ring-[#1A2F23]/20" : "border-[#E5E5E5] hover:border-[#8FA893]"}`}
                      onClick={() => setMainImage(img)}
                    >
                      <img src={img} alt={`Product ${index + 1}`} className="w-full h-full object-cover" />
                      {formData.image_url === img && <div className="absolute top-2 left-2 bg-[#1A2F23] text-white text-xs px-2 py-1 rounded">Main</div>}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); removeImage(index); }}
                        className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {formData.images.length === 0 && (
                    <div className="col-span-3 flex items-center justify-center h-32 bg-[#F5F5F4] rounded-lg text-[#9CA3AF]">No images</div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#525252] mb-2">Main Image URL</label>
                <input
                  type="text"
                  name="image_url"
                  value={formData.image_url}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-[#E5E5E5] focus:outline-none focus:ring-2 focus:ring-[#1A2F23]"
                />
              </div>
            </div>
          </div>
        </form>

        {/* Modal Footer */}
        <div className="flex items-center justify-end gap-4 p-6 border-t bg-[#F9F9F7]">
          <button type="button" onClick={onClose} className="px-6 py-3 rounded-lg border border-[#E5E5E5] text-[#525252] hover:bg-white">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !formData.name || !formData.price}
            className="flex items-center gap-2 px-6 py-3 bg-[#1A2F23] text-white rounded-lg hover:bg-[#1A2F23]/90 disabled:opacity-50"
          >
            {saving ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Saving...</> : <><Save className="w-5 h-5" />Update Product</>}
          </button>
        </div>
      </motion.div>
    </>
  );
};

// Checkout Page
const CheckoutPage = () => {
  const { cartItems, cartTotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [transactionId, setTransactionId] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    pincode: ""
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmitOrder = async () => {
    // Validate all fields
    const required = { name: "Full Name", email: "Email", phone: "Phone", address: "Address", city: "City", state: "State", pincode: "Pincode" };
    const missing = Object.entries(required).filter(([key]) => !formData[key]?.trim()).map(([, label]) => label);
    if (missing.length > 0) {
      toast.error(`Please fill: ${missing.join(", ")}`);
      return;
    }
    setLoading(true);
    try {
      const orderPayload = {
        items: cartItems.map(item => ({
          product_id: item.product_id,
          name: item.name,
          price: item.price,
          quantity: item.quantity
        })),
        customer: formData,
        subtotal: cartTotal,
        total: cartTotal
      };

      const response = await axios.post(`${API}/orders`, orderPayload);
      setOrderData(response.data);
      setStep(2);
      toast.success("Order placed! Please scan the QR code to pay.");
    } catch (error) {
      console.error("Failed to place order:", error);
      toast.error("Failed to place order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentConfirm = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/orders/${orderData.id}/confirm-payment`, {
        order_id: orderData.id
      });
      clearCart();
      toast.success("Thank you for the purchase, welcome onboard!");
      navigate("/");
    } catch (error) {
      console.error("Failed to confirm payment:", error);
      toast.error("Failed to submit. Please contact support.");
    } finally {
      setLoading(false);
    }
  };

  if (cartItems.length === 0 && !orderPlaced) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] pt-24 flex items-center justify-center" data-testid="checkout-empty">
        <div className="text-center">
          <ShoppingCart className="w-16 h-16 text-[#9CA3AF] mx-auto mb-4" />
          <p className="text-[#525252] mb-4">Your cart is empty</p>
          <Link 
            to="/products"
            className="inline-block bg-[#1A2F23] text-white px-6 py-3 rounded-full"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  if (orderPlaced) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] pt-24" data-testid="order-success">
        <div className="max-w-2xl mx-auto px-4 py-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-8 text-center shadow-lg"
          >
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-heading font-medium text-[#1A2F23] mb-4">
              Thank you for the purchase, welcome onboard!
            </h1>
            <p className="text-[#525252] mb-2">
              Your order has been placed successfully.
            </p>
            <p className="text-sm text-[#9CA3AF] mb-6">
              We'll send order updates to <strong>{formData.email}</strong>
            </p>
            <div className="bg-[#F5F5F4] p-4 rounded-xl mb-4">
              <p className="text-sm text-[#9CA3AF]">Order Number</p>
              <p className="text-lg font-semibold text-[#1A2F23]">{orderData?.order_number}</p>
            </div>
            <div className="bg-[#F5F5F4] p-4 rounded-xl mb-8">
              <p className="text-sm text-[#9CA3AF]">Transaction ID</p>
              <p className="text-lg font-semibold text-[#1A2F23]">{transactionId}</p>
            </div>
            <Link 
              to="/"
              className="inline-block bg-[#1A2F23] text-white px-8 py-4 rounded-full font-medium"
              data-testid="continue-shopping"
            >
              Continue Shopping
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7] pt-24" data-testid="checkout-page">
      <Helmet>
        <title>Checkout - Duwa Enterprises</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl md:text-4xl font-heading font-medium text-[#1A2F23] mb-8">Checkout</h1>

        {/* Progress Steps */}
        <div className="flex items-center gap-4 mb-12">
          <div className={`flex items-center gap-2 ${step >= 1 ? "text-[#1A2F23]" : "text-[#9CA3AF]"}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= 1 ? "bg-[#1A2F23] text-white" : "bg-[#E5E5E5]"}`}>1</div>
            <span className="font-medium">Shipping</span>
          </div>
          <div className="flex-1 h-0.5 bg-[#E5E5E5]">
            <div className={`h-full bg-[#1A2F23] transition-all ${step >= 2 ? "w-full" : "w-0"}`} />
          </div>
          <div className={`flex items-center gap-2 ${step >= 2 ? "text-[#1A2F23]" : "text-[#9CA3AF]"}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= 2 ? "bg-[#1A2F23] text-white" : "bg-[#E5E5E5]"}`}>2</div>
            <span className="font-medium">Payment</span>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Form / Payment Section */}
          <div className="lg:col-span-2">
            {step === 1 ? (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white rounded-2xl p-6 md:p-8 shadow-sm"
              >
                <h2 className="text-xl font-heading font-medium text-[#1A2F23] mb-6">Shipping Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-[#525252] mb-2">Full Name <span className="text-red-500">*</span></label>
                    <input 
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-[#1A2F23] ${!formData.name.trim() ? "border-red-300" : "border-[#E5E5E5]"}`}
                      placeholder="Enter your full name"
                      required
                      data-testid="input-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#525252] mb-2">Email <span className="text-red-500">*</span></label>
                    <input 
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-[#1A2F23] ${!formData.email.trim() ? "border-red-300" : "border-[#E5E5E5]"}`}
                      placeholder="your@email.com"
                      required
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#525252] mb-2">Phone <span className="text-red-500">*</span></label>
                    <input 
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-[#1A2F23] ${!formData.phone.trim() ? "border-red-300" : "border-[#E5E5E5]"}`}
                      placeholder="+91 XXXXX XXXXX"
                      required
                      data-testid="input-phone"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-[#525252] mb-2">Address <span className="text-red-500">*</span></label>
                    <textarea 
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      rows={3}
                      className={`w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-[#1A2F23] ${!formData.address.trim() ? "border-red-300" : "border-[#E5E5E5]"}`}
                      placeholder="Street address, apartment, etc."
                      required
                      data-testid="input-address"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#525252] mb-2">City <span className="text-red-500">*</span></label>
                    <input 
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-[#1A2F23] ${!formData.city.trim() ? "border-red-300" : "border-[#E5E5E5]"}`}
                      placeholder="City"
                      required
                      data-testid="input-city"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#525252] mb-2">State <span className="text-red-500">*</span></label>
                    <input 
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-[#1A2F23] ${!formData.state.trim() ? "border-red-300" : "border-[#E5E5E5]"}`}
                      placeholder="State"
                      required
                      data-testid="input-state"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#525252] mb-2">Pincode <span className="text-red-500">*</span></label>
                    <input 
                      type="text"
                      name="pincode"
                      value={formData.pincode}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-[#1A2F23] ${!formData.pincode.trim() ? "border-red-300" : "border-[#E5E5E5]"}`}
                      placeholder="XXXXXX"
                      required
                      data-testid="input-pincode"
                    />
                  </div>
                </div>
                <button 
                  onClick={handleSubmitOrder}
                  disabled={loading || !formData.name || !formData.email || !formData.phone || !formData.address || !formData.city || !formData.state || !formData.pincode}
                  className="w-full mt-8 bg-[#1A2F23] text-white py-4 rounded-full font-medium hover:bg-[#1A2F23]/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="proceed-to-payment"
                >
                  {loading ? "Processing..." : "Proceed to Payment"}
                </button>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white rounded-2xl p-6 md:p-8 shadow-sm"
              >
                <h2 className="text-xl font-heading font-medium text-[#1A2F23] mb-4">Payment via UPI</h2>
                
                {/* Amount Display - Prominent */}
                <div className="bg-gradient-to-r from-[#1A2F23] to-[#2D4A3E] text-white p-6 rounded-xl mb-6 text-center">
                  <p className="text-sm text-[#8FA893] mb-2">Amount to Pay</p>
                  <p className="text-4xl font-bold">₹{cartTotal.toFixed(2)}</p>
                  <p className="text-xs text-[#8FA893] mt-2">Scan QR code to pay exact amount</p>
                </div>
                
                <p className="text-[#525252] mb-6 text-center">Scan the QR code below with any UPI app to pay <span className="font-bold text-[#1A2F23]">₹{cartTotal.toFixed(2)}</span></p>
                
                <div className="flex justify-center mb-8">
                  <div className="bg-[#F9F9F7] p-6 rounded-xl text-center border-2 border-transparent hover:border-[#8FA893] transition-all max-w-sm w-full">
                    <div className="inline-block p-3 bg-white rounded-xl shadow-sm mb-4">
                      <QRCodeSVG 
                        value={generateUPIUrl(UPI_ID, PAYEE_NAME, cartTotal)}
                        size={200}
                        level="H"
                        includeMargin={true}
                        bgColor="#ffffff"
                        fgColor="#1A2F23"
                      />
                    </div>
                    <p className="font-semibold text-[#1A2F23] text-lg mb-1">PhonePe Business</p>
                    <p className="text-sm text-[#9CA3AF] mb-3">UPI: {UPI_ID}</p>
                    <div className="bg-[#1A2F23] text-white px-4 py-2 rounded-lg text-sm font-medium">
                      ₹{cartTotal.toFixed(2)}
                    </div>
                    <p className="text-xs text-green-600 mt-2 font-medium">Amount is pre-filled — just scan & pay</p>
                    <a 
                      href={generateUPIUrl(UPI_ID, PAYEE_NAME, cartTotal)}
                      className="inline-flex items-center justify-center gap-2 mt-3 w-full bg-[#5f259f] text-white py-3 rounded-lg font-medium hover:bg-[#4a1d7a] transition-colors"
                      data-testid="open-upi-app"
                    >
                      Open in UPI App →
                    </a>
                    <p className="text-xs text-[#9CA3AF] mt-2">Works on mobile with PhonePe, Google Pay, Paytm or any UPI app</p>
                  </div>
                </div>

                <div className="bg-green-50 border border-green-200 p-4 rounded-xl mb-4">
                  <p className="text-sm text-green-800">
                    <strong>✓ Amount Pre-filled:</strong> When you scan, the amount ₹{cartTotal.toFixed(2)} will be automatically filled in your UPI app. Just verify and pay!
                  </p>
                </div>

                <div className="bg-amber-50 border border-amber-200 p-4 rounded-xl mb-6">
                  <p className="text-sm text-amber-800">
                    <strong>Note:</strong> If QR code is not accepting the payments above 2,000 INR, pay using the above mentioned UPI ID or Contact number. Thank You!
                  </p>
                </div>
                
                <Link 
                  to="/"
                  onClick={() => clearCart()}
                  className="block w-full text-center bg-[#1A2F23] text-white py-4 rounded-full font-medium hover:bg-[#1A2F23]/90 transition-all mt-4"
                  data-testid="continue-shopping-checkout"
                >
                  Continue Shopping
                </Link>
                
                <button 
                  onClick={() => setStep(1)}
                  className="w-full mt-4 text-[#525252] py-2"
                >
                  Back to Shipping Details
                </button>
              </motion.div>
            )}
          </div>

          {/* Order Summary */}
          <div>
            <div className="bg-white rounded-2xl p-6 shadow-sm sticky top-28">
              <h2 className="text-xl font-heading font-medium text-[#1A2F23] mb-6">Order Summary</h2>
              <div className="space-y-4 mb-6">
                {cartItems.map(item => (
                  <div key={item.product_id} className="flex gap-4">
                    <img 
                      src={item.image_url} 
                      alt={item.name}
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-sm text-[#1A1A1A] line-clamp-2">{item.name}</p>
                      <p className="text-sm text-[#9CA3AF]">Qty: {item.quantity}</p>
                    </div>
                    <p className="font-medium text-[#1A2F23]">₹{(item.price * item.quantity).toFixed(2)}</p>
                  </div>
                ))}
              </div>
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-[#525252]">
                  <span>Subtotal</span>
                  <span>₹{cartTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-[#525252]">
                  <span>Shipping</span>
                  <span className="text-green-600">Free</span>
                </div>
                <div className="flex justify-between text-lg font-semibold text-[#1A2F23] pt-2 border-t">
                  <span>Total</span>
                  <span>₹{cartTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// About Page
const AboutPage = () => {
  return (
    <div className="min-h-screen bg-[#F9F9F7] pt-24" data-testid="about-page">
      <Helmet>
        <title>About Duwa Enterprises - Our Story | Herbal Hair Care</title>
        <meta name="description" content="Learn about Duwa Enterprises - your trusted source for authentic herbal hair care products. Rooted in Ayurvedic wisdom, enhanced with modern research. 100% natural, cruelty-free, eco-conscious." />
        <link rel="canonical" href={`${window.location.origin}/about`} />
        <meta property="og:title" content="About Duwa Enterprises - Our Story" />
        <meta property="og:description" content="Your trusted source for authentic herbal hair care products. Rooted in Ayurvedic wisdom." />
        <meta property="og:url" content={`${window.location.origin}/about`} />
      </Helmet>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-4xl md:text-5xl font-heading font-medium text-[#1A2F23] mb-8">About Duwa Enterprises</h1>
          <div className="prose prose-lg max-w-none">
            <p className="text-[#525252] text-lg leading-relaxed mb-6">
              At Duwa Enterprises, we believe in the power of nature. Our journey began with a simple mission: to bring authentic, herbal hair care solutions to every home in India.
            </p>
            <p className="text-[#525252] text-lg leading-relaxed mb-6">
              Rooted in traditional Ayurvedic wisdom and enhanced with modern research, our products are carefully formulated to address common hair concerns like hair fall, thinning, and scalp health—all while keeping your hair's natural beauty intact.
            </p>
            <div className="bg-white p-8 rounded-2xl my-8">
              <h2 className="text-2xl font-heading font-medium text-[#1A2F23] mb-4">Our Promise</h2>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-[#8FA893] mt-0.5" />
                  <span className="text-[#525252]">100% natural and herbal ingredients</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-[#8FA893] mt-0.5" />
                  <span className="text-[#525252]">No harmful chemicals or parabens</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-[#8FA893] mt-0.5" />
                  <span className="text-[#525252]">Suitable for all hair types</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-[#8FA893] mt-0.5" />
                  <span className="text-[#525252]">Cruelty-free and eco-conscious</span>
                </li>
              </ul>
            </div>
            <p className="text-[#525252] text-lg leading-relaxed">
              Join thousands of satisfied customers who have transformed their hair health with our herbal products. Experience the Duwa difference today.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

// Contact Page
const ContactPage = () => {
  return (
    <div className="min-h-screen bg-[#F9F9F7] pt-24" data-testid="contact-page">
      <Helmet>
        <title>Contact Duwa Enterprises - Get in Touch | Herbal Hair Care</title>
        <meta name="description" content="Contact Duwa Enterprises for herbal hair care products. Visit us at Hubli, Karnataka or call +91 6398806603. Email: asrafsalmani98@gmail.com. We're here to help!" />
        <link rel="canonical" href={`${window.location.origin}/contact`} />
        <meta property="og:title" content="Contact Duwa Enterprises" />
        <meta property="og:description" content="Get in touch with Duwa Enterprises for herbal hair care products. Call, email or visit us in Hubli." />
        <meta property="og:url" content={`${window.location.origin}/contact`} />
      </Helmet>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-4xl md:text-5xl font-heading font-medium text-[#1A2F23] mb-8">Contact Us</h1>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-2xl">
              <h2 className="text-xl font-heading font-medium text-[#1A2F23] mb-6">Get in Touch</h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#8FA893]/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-[#1A2F23]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#1A1A1A]">Phone</p>
                    <a href="tel:+916398806603" className="text-[#525252] hover:text-[#8FA893]">+91 6398806603</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#8FA893]/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#1A2F23]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#1A1A1A]">Email</p>
                    <a href="mailto:asrafsalmani98@gmail.com" className="text-[#525252] hover:text-[#8FA893]">asrafsalmani98@gmail.com</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#8FA893]/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#1A2F23]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#1A1A1A]">Address</p>
                    <p className="text-[#525252]">
                      Big Boss 2 Hair Salon<br />
                      Arvind Nagar Karwar Road<br />
                      Near ESI Hospital, Hubli<br />
                      Karnataka - 580024
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden relative" data-testid="contact-image">
              <img 
                src="https://images.unsplash.com/photo-1766066014237-00645c74e9c6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxmZW1hbGUlMjBjdXN0b21lciUyMHN1cHBvcnQlMjBhZ2VudCUyMGhlYWRzZXQlMjBzbWlsaW5nfGVufDB8fHx8MTc3MjAyNDcyMHww&ixlib=rb-4.1.0&q=85" 
                alt="Customer support agent ready to help" 
                className="w-full h-full object-cover min-h-[360px]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A2F23]/80 to-transparent flex flex-col justify-end p-8">
                <p className="text-white font-heading text-xl font-medium mb-2">We're Here to Help</p>
                <p className="text-[#C5D5C9] text-sm">Reach out to us anytime — our team is always happy to assist you.</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="bg-[#1A2F23] text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          <div className="md:col-span-2">
            <div className="bg-white p-3 rounded-lg inline-block mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_design-hosting-flow/artifacts/bui4e431_ChatGPT%20Image%20Feb%2025%2C%202026%2C%2007_13_10%20AM.png" 
                alt="Duwa Enterprises" 
                className="h-12 w-auto"
              />
            </div>
            <p className="text-[#8FA893] mb-6 max-w-md">
              Pure Herbs. Real Care. Visible Results. Discover the power of nature with our authentic herbal hair care products.
            </p>
            <div className="flex items-center gap-4">
              <a 
                href="https://wa.me/916398806603" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
              >
                <Phone className="w-5 h-5" />
              </a>
              <a 
                href="mailto:asrafsalmani98@gmail.com"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
              >
                <Mail className="w-5 h-5" />
              </a>
            </div>
          </div>
          <div>
            <h3 className="font-medium mb-4">Quick Links</h3>
            <ul className="space-y-3">
              <li><Link to="/" className="text-[#8FA893] hover:text-white transition-colors">Home</Link></li>
              <li><Link to="/products" className="text-[#8FA893] hover:text-white transition-colors">Products</Link></li>
              <li><Link to="/about" className="text-[#8FA893] hover:text-white transition-colors">About Us</Link></li>
              <li><Link to="/contact" className="text-[#8FA893] hover:text-white transition-colors">Contact</Link></li>
            </ul>
          </div>
          <div>
            <h3 className="font-medium mb-4">Contact Info</h3>
            <ul className="space-y-3 text-[#8FA893]">
              <li>+91 6398806603</li>
              <li>asrafsalmani98@gmail.com</li>
              <li>Big Boss 2 Hair Salon<br />Arvind Nagar Karwar Road<br />Near ESI Hospital, Hubli<br />Karnataka - 580024</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 mt-12 pt-8 text-center text-[#8FA893] text-sm">
          <p>© 2026 Duwa Enterprises. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

// Layout wrapper to conditionally show header/footer
const Layout = ({ children }) => {
  const location = useLocation();
  const isAdminPage = location.pathname.startsWith('/admin');
  
  return (
    <>
      {!isAdminPage && <Header />}
      {!isAdminPage && <CartDrawer />}
      {children}
      {!isAdminPage && <Footer />}
    </>
  );
};

// Main App Component
function App() {
  // Hide platform badge
  useEffect(() => {
    const hideBadge = () => {
      const badge = document.getElementById('emergent-badge');
      if (badge) badge.remove();
    };
    hideBadge();
    const observer = new MutationObserver(hideBadge);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return (
    <CartProvider>
      <div className="App">
        <BrowserRouter>
          <Layout>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/product/:slug" element={<ProductDetailPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/admin" element={<AdminDashboard />} />
            </Routes>
          </Layout>
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </div>
    </CartProvider>
  );
}

export default App;
